import { useState, useEffect, useContext, useMemo } from 'react';
import { MessageContext } from '../data/MessageContext';

import {
    globalHandleMessage,
    processMessage,
    processVideoClips,
    parsePayloadFromJWT,
} from '../utils/helper';

import { MessageData } from '../interface/interface';

let pubnubInitialized = false;

export function usePubnubMessages(
    pubnub: any,
    channels: any,
    cancelAlarmActiveHandler: Function,
    reqPolActive: Function,
    handleIncidentEvent: Function,
) {
    const [rawMessages, setRawMessages] = useState<any[]>([]);

    const localAuthToken = localStorage.getItem('api_Token');
    const smartAuth = parsePayloadFromJWT(localAuthToken);

    const currentUserDetails = {
        id: smartAuth?.token,
        name: smartAuth?.name,
    };

    const { updateMessageData } = useContext(MessageContext)!;

    useEffect(() => {
        const listenerParams = {
            message: (message: any) => {
                globalHandleMessage(
                    message,
                    updateMessageData,
                    cancelAlarmActiveHandler,
                    reqPolActive,
                    handleIncidentEvent,
                );
                setRawMessages((messages: any) => [...messages, message]);
            },
        };
        if (pubnub && channels?.length && !pubnubInitialized) {
            pubnub.addListener(listenerParams);
            pubnub.subscribe({ channels, withPresence: true });
            pubnubInitialized = true;
        }
    }, [
        pubnub,
        channels,
        cancelAlarmActiveHandler,
        reqPolActive,
        handleIncidentEvent,
        updateMessageData,
    ]);

    const filteredMessages = useMemo(() => {
        return processMessages(rawMessages);
    }, [rawMessages]);

    // Main useEffect function
    useEffect(() => {
        if (!channels) {
            return;
        }

        const fetchAllHistory = async () => {
            await Promise.all(
                channels.map(async (channel: string) => {
                    await fetchHistoryRecursive(
                        channel,
                        0,
                        0,
                        pubnub,
                        setRawMessages,
                    );
                }),
            );
        };
        fetchAllHistory();
    }, [pubnub, channels]);

    const sendMessage = async (messageString: string) => {
        if (!channels) {
            return;
        }

        if (messageString) {
            const message = {
                type: 'EVENT_MESSAGE',
                payload: {
                    event_type: 'EVENT_MESSAGE',
                    sender: {
                        name: currentUserDetails.name,
                        token: currentUserDetails.id,
                    },
                    body: messageString,
                },
            };

            pubnub.publish({ channel: channels[0], message });
        }
    };

    return { rawMessages, messages: filteredMessages, sendMessage };
}

// Helper function to fetch messages for a given channel
const fetchChannelMessages = async (
    channel: string,
    startTimeToken: number,
    totalFetched: number,
    pubnub: any,
) => {
    const messagesToFetch = Math.min(100 - totalFetched, 100);
    const response = await pubnub.fetchMessages({
        channels: [channel],
        count: messagesToFetch,
        stringifiedTimeToken: true,
        includeMeta: true,
        includeMessageActions: true,
        start: startTimeToken,
    });
    return response;
};

const fetchHistoryRecursive = async (
    channel: string,
    startTimeToken: number,
    totalFetched: number,
    pubnub: any,
    setRawMessages: Function,
) => {
    const response = await fetchChannelMessages(
        channel,
        startTimeToken,
        totalFetched,
        pubnub,
    );

    if (!response || !Object.keys(response.channels).length) {
        return;
    }

    const newMessages = response.channels[channel];
    if (!newMessages) return;

    if (newMessages.length) {
        setRawMessages((oldData: Array<any>) => [...oldData, ...newMessages]);
    }

    const fetchedMessageCount = newMessages.length;
    if (totalFetched + fetchedMessageCount < 100) {
        const lastMessageTimeToken = getLastMessageTimeToken(
            response.channels[channel],
        );
        if (lastMessageTimeToken) {
            await fetchHistoryRecursive(
                channel,
                lastMessageTimeToken,
                totalFetched + fetchedMessageCount + 1,
                pubnub,
                setRawMessages,
            );
        }
    }
};

const getLastMessageTimeToken = (channelData: any[]): number | undefined => {
    return channelData[0]?.timetoken;
};

const processMessages = (messages: any) => {
    return messages
        ?.map((m: any) => {
            if (m.channel.includes('clip')) {
                return processVideoClips(m);
            } else if (m.message?.type && m.message?.payload?.sender) {
                return processMessage(m);
            } else if (!m.message?.payload?.sender && m.message?.type) {
                return {
                    type: m.message.type || '',
                    contacts: m.message.payload.contacts,
                };
            }
            return undefined;
        })
        .filter((m: any) => m !== undefined)
        .sort((a: MessageData, b: MessageData) => {
            // Fallback to Unix epoch start if undefined
            const timeA = BigInt(a.msgTime ?? '0');
            const timeB = BigInt(b.msgTime ?? '0');
            if (timeA > timeB) return -1; // For descending order
            if (timeA < timeB) return 1;
            return 0;
        });
};
